/**
 * La marca de una tarjeta, dibujada y no escrita.
 *
 * En la ficha de un cobro «VISA ···· 4242» obliga a leer para reconocer algo que
 * se reconoce de un vistazo, y encima pone en mayúsculas una palabra que compite
 * con el importe. Con la marca dibujada el ojo la resuelve sin leer y la fila
 * queda para el número, que es el dato que distingue una tarjeta de otra.
 *
 * Son trazos propios y mínimos, no los logotipos oficiales: éstos van dentro de
 * un rectángulo neutro, en un solo tamaño y sin sus colores de marca cuando el
 * color no aporta —el mismo trato que reciben en un recibo—. Las marcas que no
 * están dibujadas caen en el texto de siempre, que es una salida honesta: mejor
 * decir «UNIONPAY» que inventarle un logotipo.
 */

/** Las que Stripe nombra en `card.brand`, con el nombre que se enseña. */
const BRAND_LABEL: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  discover: 'Discover',
  diners: 'Diners Club',
  jcb: 'JCB',
  unionpay: 'UnionPay',
  eftpos_au: 'Eftpos',
  link: 'Link',
}

export function cardBrandLabel(brand: string): string {
  return BRAND_LABEL[brand.toLowerCase()] ?? brand.toUpperCase()
}

/**
 * El dibujo de cada marca dentro de la misma caja de 32×20.
 *
 * Todas comparten marco para que la fila no baile cuando un comprador paga con
 * una y el siguiente con otra.
 */
function BrandArt({ brand }: { brand: string }) {
  if (brand === 'visa') {
    return (
      <text x="16" y="14.5" textAnchor="middle" fontSize="9.5" fontWeight="700" fontStyle="italic" fill="#1434CB" fontFamily="Helvetica, Arial, sans-serif">
        VISA
      </text>
    )
  }

  if (brand === 'mastercard') {
    return (
      <>
        {/* Los dos círculos entrelazados: el de en medio es la intersección. */}
        <circle cx="13" cy="10" r="5.5" fill="#EB001B" />
        <circle cx="19" cy="10" r="5.5" fill="#F79E1B" />
        <path d="M16 5.9a5.5 5.5 0 0 0 0 8.2 5.5 5.5 0 0 0 0-8.2Z" fill="#FF5F00" />
      </>
    )
  }

  if (brand === 'amex') {
    return (
      <>
        <rect x="3" y="3" width="26" height="14" rx="2" fill="#1F72CD" />
        <text x="16" y="12.5" textAnchor="middle" fontSize="6.5" fontWeight="700" fill="#ffffff" fontFamily="Helvetica, Arial, sans-serif">
          AMEX
        </text>
      </>
    )
  }

  if (brand === 'discover') {
    return (
      <>
        <text x="12" y="13.5" textAnchor="middle" fontSize="6" fontWeight="700" fill="currentColor" fontFamily="Helvetica, Arial, sans-serif">
          DISC
        </text>
        <circle cx="24" cy="11" r="3.4" fill="#FF6000" />
      </>
    )
  }

  // Sin dibujo propio: el nombre, encajado en la misma caja.
  return (
    <text x="16" y="13.5" textAnchor="middle" fontSize="6" fontWeight="700" fill="currentColor" fontFamily="Helvetica, Arial, sans-serif">
      {cardBrandLabel(brand).slice(0, 10).toUpperCase()}
    </text>
  )
}

export function CardMark({ brand }: { brand: string }) {
  const key = brand.toLowerCase()
  return (
    <span className="dash-card-mark" aria-hidden="true">
      <svg width="32" height="20" viewBox="0 0 32 20" role="presentation" focusable="false">
        <rect x="0.5" y="0.5" width="31" height="19" rx="3.5" fill="var(--dash-panel)" stroke="var(--dash-border)" />
        <BrandArt brand={key} />
      </svg>
    </span>
  )
}
